import { type ComponentType, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  GraphMeeting,
  GraphUnreadMailMessage,
  ProjectPulse,
  WaitingThread,
} from "@/api/home-context"
import type { TFunction } from "i18next"

import { CalendarClock, MoveRight } from "@/components/icons"
import { Button } from "@/components/ui/button"

function formatStartTime(iso: string | null | undefined): string {
  if (!iso) return ""
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return ""
  return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

function minutesUntil(iso: string | null | undefined): number {
  if (!iso) return 0
  const start = new Date(iso).getTime()
  if (Number.isNaN(start)) return 0
  return Math.max(0, Math.round((start - Date.now()) / 60000))
}

function isOngoingMeeting(meeting: GraphMeeting): boolean {
  if (!meeting.start_iso || !meeting.end_iso) return false
  const now = Date.now()
  const start = new Date(meeting.start_iso).getTime()
  const end = new Date(meeting.end_iso).getTime()
  return Number.isFinite(start) && Number.isFinite(end) && start <= now && end >= now
}

export function attendeeSummary(meeting: GraphMeeting): string {
  const names = meeting.attendees
    .map((a) => (a.name ? a.name.split(" ")[0] : null))
    .filter((n): n is string => Boolean(n))
  if (names.length === 0) return ""
  if (names.length <= 2) return names.join(", ")
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`
}

export function formatRelative(iso: string | null | undefined, locale: string): string {
  if (!iso) return ""
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return ""
  const diffSec = Math.round((ms - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat(locale || "en", { numeric: "auto" })
  if (abs < 60) return rtf.format(diffSec, "second")
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute")
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour")
  if (abs < 604800) return rtf.format(Math.round(diffSec / 86400), "day")
  return rtf.format(Math.round(diffSec / 604800), "week")
}

function inboxPrompt(msg: GraphUnreadMailMessage, t: TFunction): string {
  const subject =
    msg.subject ?? t("chatHome.dayBrief.untitledEmail", { defaultValue: "Untitled email" })
  const sender = msg.sender ? ` from ${msg.sender}` : ""
  return t("chatHome.dayBrief.prompts.replyEmail", {
    defaultValue:
      'Help me draft a reply to this email and tell me what it needs: "{{subject}}"{{sender}}.',
    subject,
    sender,
  })
}

function projectPulsePrompt(pulse: ProjectPulse, t: TFunction): string {
  return t("chatHome.dayBrief.prompts.projectPulse", {
    defaultValue:
      "Summarize the current status of {{name}} — surface what stalled, what's next, and what I should chase.",
    name: pulse.name,
  })
}

function waitingThreadPrompt(thread: WaitingThread, t: TFunction): string {
  const sender =
    thread.sender ?? t("chatHome.dayBrief.unknownSender", { defaultValue: "this collaborator" })
  const subject = thread.subject ?? ""
  return t("chatHome.dayBrief.prompts.replyWaiting", {
    defaultValue: 'Draft a reply to {{sender}} about "{{subject}}".',
    sender,
    subject,
  })
}

type BriefRowProps = {
  Leading?: ComponentType<{ className?: string }>
  leadingGlyph?: string
  title: string
  subtitle?: string | null
  ctaLabel: string
  onClick: () => void
}

function BriefRow({ Leading, leadingGlyph, title, subtitle, ctaLabel, onClick }: BriefRowProps) {
  return (
    <button
      className="bg-background/70 hover:bg-background/90 focus-visible:ring-ring flex w-full items-center justify-between gap-6 rounded-md px-5 py-3.5 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
      type="button"
      onClick={onClick}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {Leading ? <Leading className="text-primary/80 size-4 shrink-0" /> : null}
        {leadingGlyph ? (
          <span aria-hidden="true" className="text-warning-ink shrink-0 text-base leading-none">
            {leadingGlyph}
          </span>
        ) : null}
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-foreground text-sm leading-tight font-medium">{title}</p>
          {subtitle ? (
            <p className="text-muted-foreground line-clamp-1 text-xs">{subtitle}</p>
          ) : null}
        </div>
      </div>
      <span className="text-primary inline-flex shrink-0 items-center gap-1 text-xs font-medium whitespace-nowrap">
        {ctaLabel}
        <MoveRight className="size-3" />
      </span>
    </button>
  )
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-muted-foreground mb-3 text-xs font-semibold tracking-wider uppercase">
      {children}
    </p>
  )
}

export function RightNowBlock({
  meeting,
  compact,
  onPrep,
}: {
  meeting: GraphMeeting
  compact: boolean
  onPrep?: (meeting: GraphMeeting) => void
}) {
  const { t } = useTranslation()
  const [minutes, setMinutes] = useState(() => minutesUntil(meeting.start_iso))
  useEffect(() => {
    const id = window.setInterval(() => setMinutes(minutesUntil(meeting.start_iso)), 30_000)
    return () => window.clearInterval(id)
  }, [meeting.start_iso])

  const ongoing = isOngoingMeeting(meeting)
  const startLabel = formatStartTime(meeting.start_iso)
  const attendees = attendeeSummary(meeting)

  const ongoingLabel = t("chatHome.meetingHero.ongoingLabel", "Ongoing")
  const upNextLabel = t("chatHome.dayBrief.upNextLabel", { defaultValue: "Up next" })
  const subjectText = meeting.subject ?? t("chatHome.meetingHero.untitled", "Untitled meeting")

  return (
    <div className={compact ? "flex flex-col gap-3" : "flex items-start gap-5"}>
      <div className={compact ? "flex items-start gap-3" : "flex flex-1 items-start gap-4"}>
        <CalendarClock className="text-primary mt-1 size-5 shrink-0" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="text-foreground/80 text-xs font-semibold tracking-wider uppercase">
            {ongoing ? ongoingLabel : upNextLabel}
          </p>
          <h2 className="text-lg leading-tight font-semibold">
            {subjectText}
            {!ongoing && startLabel ? (
              <span className="text-foreground/70"> — {startLabel}</span>
            ) : null}
          </h2>
          {attendees ? (
            <p className="text-muted-foreground text-sm">
              {t("chatHome.meetingHero.with", "With")} {attendees}
              {minutes > 0 && !ongoing ? (
                <>
                  {" "}
                  &middot; {t("chatHome.meetingHero.inMinutes", "in {{minutes}} min", { minutes })}
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      </div>
      <div
        className={compact ? "flex flex-wrap gap-2" : "flex shrink-0 flex-col items-stretch gap-2"}
      >
        <Button
          data-testid="day-brief-prep"
          size={compact ? "sm" : "default"}
          onClick={() => onPrep?.(meeting)}
        >
          {ongoing
            ? t("chatHome.meetingHero.catchUp", "Catch me up →")
            : t("chatHome.meetingHero.prep", "Prep me for this meeting →")}
        </Button>
      </div>
    </div>
  )
}

export function TodayStackSection({
  meetings,
  onMeetingPrep,
}: {
  meetings: GraphMeeting[]
  onMeetingPrep?: (meeting: GraphMeeting) => void
}) {
  const { t } = useTranslation()
  return (
    <div>
      <SectionLabel>
        {t("chatHome.dayBrief.todayStackLabel", {
          count: meetings.length,
          defaultValue: "Later today · {{count}} meetings",
        })}
      </SectionLabel>
      <div className="space-y-3">
        {meetings.map((m) => (
          <BriefRow
            key={m.id}
            ctaLabel={t("chatHome.dayBrief.prepShort", { defaultValue: "Prep" })}
            subtitle={attendeeSummary(m) || (m.preview ?? null)}
            title={`${formatStartTime(m.start_iso)} · ${
              m.subject ?? t("chatHome.meetingHero.untitled", "Untitled meeting")
            }`}
            onClick={() => onMeetingPrep?.(m)}
          />
        ))}
      </div>
    </div>
  )
}

export function InboxSection({
  highlights,
  onPromptClick,
}: {
  highlights: GraphUnreadMailMessage[]
  onPromptClick: (prompt: string) => void
}) {
  const { t, i18n } = useTranslation()
  return (
    <div>
      <SectionLabel>
        {t("chatHome.dayBrief.inboxLabel", {
          count: highlights.length,
          defaultValue: "Inbox · {{count}} pressing",
        })}
      </SectionLabel>
      <div className="space-y-3">
        {highlights.map((msg) => (
          <BriefRow
            key={msg.id}
            ctaLabel={t("chatHome.dayBrief.replyShort", { defaultValue: "Draft reply" })}
            leadingGlyph="★"
            subtitle={
              msg.sender
                ? `${msg.sender} · ${formatRelative(msg.received_iso, i18n.language)}`
                : null
            }
            title={
              msg.subject ??
              t("chatHome.dayBrief.untitledEmail", { defaultValue: "Untitled email" })
            }
            onClick={() => onPromptClick(inboxPrompt(msg, t))}
          />
        ))}
      </div>
    </div>
  )
}

export function ProjectPulseSection({
  pulse,
  onPromptClick,
}: {
  pulse: ProjectPulse
  onPromptClick: (prompt: string) => void
}) {
  const { t, i18n } = useTranslation()
  return (
    <div>
      <SectionLabel>
        {t("chatHome.dayBrief.projectPulseLabel", {
          defaultValue: "Project pulse · needs attention",
        })}
      </SectionLabel>
      <BriefRow
        ctaLabel={t("chatHome.dayBrief.summarizeShort", { defaultValue: "Summarize" })}
        title={pulse.name}
        subtitle={
          pulse.updated_iso
            ? t("chatHome.dayBrief.lastActive", {
                defaultValue: "Last active {{when}}",
                when: formatRelative(pulse.updated_iso, i18n.language),
              })
            : null
        }
        onClick={() => onPromptClick(projectPulsePrompt(pulse, t))}
      />
    </div>
  )
}

export function WaitingSection({
  threads,
  onPromptClick,
}: {
  threads: WaitingThread[]
  onPromptClick: (prompt: string) => void
}) {
  const { t, i18n } = useTranslation()
  return (
    <div>
      <SectionLabel>
        {t("chatHome.dayBrief.waitingLabel", { defaultValue: "Waiting on you" })}
      </SectionLabel>
      <div className="space-y-3">
        {threads.map((thread) => {
          const fallbackTitle =
            thread.subject ??
            t("chatHome.dayBrief.unknownThread", { defaultValue: "Unfollowed thread" })
          const title = thread.sender
            ? t("chatHome.dayBrief.waitingFrom", {
                defaultValue: "{{sender}} is waiting on a reply",
                sender: thread.sender,
              })
            : fallbackTitle
          const subtitle = thread.received_iso
            ? t("chatHome.dayBrief.lastSent", {
                defaultValue: "Last message {{when}}",
                when: formatRelative(thread.received_iso, i18n.language),
              })
            : null
          return (
            <BriefRow
              key={thread.id ?? thread.subject ?? thread.sender ?? "waiting"}
              ctaLabel={t("chatHome.dayBrief.replyShort", { defaultValue: "Reply" })}
              subtitle={subtitle}
              title={title}
              onClick={() => onPromptClick(waitingThreadPrompt(thread, t))}
            />
          )
        })}
      </div>
    </div>
  )
}

export function TomorrowSection({
  meetings,
  compact,
}: {
  meetings: GraphMeeting[]
  compact: boolean
}) {
  const { t } = useTranslation()
  if (meetings.length === 0) return null
  const first = meetings[0]
  const firstTime = formatStartTime(first.start_iso)
  const firstWith = attendeeSummary(first)
  const summary = firstWith
    ? t("chatHome.dayBrief.tomorrowWithName", {
        defaultValue: "{{count}} meetings, first at {{time}} with {{name}}",
        count: meetings.length,
        time: firstTime,
        name: firstWith,
      })
    : t("chatHome.dayBrief.tomorrow", {
        defaultValue: "{{count}} meetings, first at {{time}}",
        count: meetings.length,
        time: firstTime,
      })
  return (
    <div
      className={
        compact ? "border-foreground/10 border-t-2 pt-5" : "border-foreground/10 border-t-2 pt-6"
      }
    >
      <SectionLabel>
        {t("chatHome.dayBrief.tomorrowLabel", { defaultValue: "Tomorrow" })}
      </SectionLabel>
      <p className="text-sm leading-relaxed">{summary}</p>
    </div>
  )
}
